import { SONNER_TOAST_CLASSNAMES } from "@flowstate/ui"
import { useTheme } from "next-themes"
import { Toaster as Sonner, toast as sonnerToast } from "sonner"
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react"
import { notificationPreferencesService } from "@/services/core/notifications/notification-preferences.service"
import { useAuth } from "@/hooks/useAuth"
import { logger } from "@/lib/logger"

type ToasterProps = React.ComponentProps<typeof Sonner>

interface ToastDurationContextType {
  duration: number
  setDuration: (ms: number) => void
  errorDuration: number
}

const ToastDurationContext = createContext<ToastDurationContextType>({
  duration: 3000,
  setDuration: () => {},
  errorDuration: Number.POSITIVE_INFINITY,
})

export const useToastDuration = () => useContext(ToastDurationContext)

interface ToastProviderProps {
  children: ReactNode
}

/**
 * Toast Duration Provider - loads user preference for toast duration
 * UX: Success toasts use user preference (default 3s); errors persist until dismissed
 */
export const ToastDurationProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const { profile } = useAuth()
  const [duration, setDurationState] = useState(3000)
  const [errorDuration] = useState(Number.POSITIVE_INFINITY)

  useEffect(() => {
    const loadDuration = async () => {
      if (!profile?.id) {
        return
      }
      
      const prefs = await notificationPreferencesService.getPreferencesWithDefaults(profile.id)
      if (prefs.success && prefs.data?.toast_duration) {
        setDurationState(prefs.data.toast_duration)
      }
    }
    
    loadDuration()
  }, [profile?.id])

  const setDuration = useCallback((ms: number) => {
    setDurationState(ms)
    // Persist to user preferences
    if (profile?.id) {
      notificationPreferencesService.updatePreferences({ toast_duration: ms }).catch((err) => logger.error('Failed to save toast duration', { error: err }))
    }
  }, [profile?.id])

  return (
    <ToastDurationContext.Provider value={{ duration, setDuration, errorDuration }}>
      {children}
    </ToastDurationContext.Provider>
  )
}

/**
 * Standard toast for flowstate-automate. Use: import { toast } from "@/components/ui/sonner".
 * Matches tradiegate/other apps: same classNames (background, border, primary/muted), bottom-right position.
 * UX: Success toasts use user preference (default 3s); errors persist until dismissed.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()
  const { duration } = useToastDuration()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      position="top-right"
      className="toaster group"
      closeButton
      toastOptions={{
        duration,
        classNames: SONNER_TOAST_CLASSNAMES,
      }}
      {...props}
    />
  )
}

/** Wrapped toast: error toasts persist until dismissed; success/default use user preference */
const toast = {
  ...sonnerToast,
  error: (message: React.ReactNode, opts?: Parameters<typeof sonnerToast.error>[1]) =>
    sonnerToast.error(message, { ...opts, duration: Number.POSITIVE_INFINITY }),
};

// Re-export sonnerToast for files that need the original (e.g., with custom duration options)
export { Toaster, toast, sonnerToast }
